import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import type { ApprovalItem, ReviewStatus } from '@/types/gallery';

export interface GalleryApprovalHook {
  pendingGalleries: ApprovalItem[];
  isLoading: boolean;
  error: string | null;
  fetchPendingGalleries: () => Promise<ApprovalItem[]>;
  approveGallery: (galleryId: number, reason?: string) => Promise<boolean>;
  rejectGallery: (galleryId: number, reason?: string) => Promise<boolean>;
  flagGallery: (galleryId: number, reason?: string) => Promise<boolean>;
  updateGalleryStatus: (galleryId: number, newStatus: ReviewStatus, reason?: string) => Promise<boolean>;
}

export const useGalleryApprovals = (): GalleryApprovalHook => {
  const [pendingGalleries, setPendingGalleries] = useState<ApprovalItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchPendingGalleries = useCallback(async (): Promise<ApprovalItem[]> => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🔍 Fetching pending galleries...');
      
      // Try to fetch from gallery_complete view first
      const { data: galleryCompleteData, error: galleryCompleteError } = await supabase
        .from('gallery_complete')
        .select('*')
        .eq('review_status', 'pending')
        .order('created_on', { ascending: false })
        .limit(100);
      
      if (!galleryCompleteError && galleryCompleteData) {
        console.log('✅ Found galleries in gallery_complete view:', galleryCompleteData.length);
        
        // Transform gallery_complete data to ApprovalItem format
        const transformedGalleries: ApprovalItem[] = galleryCompleteData.map(gallery => ({
          id: gallery.gallery_id!,
          type: 'gallery' as const,
          title: gallery.gallery_title || 'Untitled Gallery',
          description: gallery.gallery_description,
          image_url: gallery.cover_image,
          franchise_region: gallery.franchise_region || 'general',
          review_status: gallery.review_status || 'pending',
          created_at: gallery.created_on || new Date().toISOString(),
          job_number: gallery.job_number,
          service_name: gallery.primary_service,
          metadata: {
            is_public: gallery.is_public,
            services: gallery.services,
            tags: gallery.tags
          }
        }));
        
        setPendingGalleries(transformedGalleries);
        setIsLoading(false);
        return transformedGalleries;
      }
      
      console.warn('❌ gallery_complete view not available:', galleryCompleteError?.message);
      
      // Fallback to direct galleries table
      const { data: galleriesData, error: galleriesError } = await supabase
        .from('galleries')
        .select('*')
        .eq('review_status', 'pending')
        .order('created_on', { ascending: false })
        .limit(100);
      
      if (galleriesError) {
        console.warn('❌ galleries table not available:', galleriesError.message);
        
        // If both tables are missing, return empty array gracefully
        console.log('📝 No gallery tables available - returning empty array');
        setPendingGalleries([]);
        setIsLoading(false);
        return [];
      }
      
      console.log('✅ Found galleries in galleries table:', galleriesData?.length || 0);
      
      // Transform galleries data to ApprovalItem format
      const transformedGalleries: ApprovalItem[] = (galleriesData || []).map(gallery => ({
        id: gallery.id,
        type: 'gallery' as const,
        title: gallery.title || 'Untitled Gallery',
        description: gallery.description,
        image_url: gallery.cover_image,
        franchise_region: gallery.franchise_region || 'general',
        review_status: gallery.review_status || 'pending',
        created_at: gallery.created_on || new Date().toISOString(),
        job_number: gallery.job_number,
        service_name: gallery.primary_service,
        metadata: {
          is_public: gallery.is_public,
          used_for_social_media: gallery.used_for_social_media
        }
      }));
      
      setPendingGalleries(transformedGalleries);
      setIsLoading(false);
      return transformedGalleries;
    } catch (error) {
      console.error('Error fetching pending galleries:', error);
      
      // Don't show error toast for missing tables - this is expected during setup
      setError(null);
      setPendingGalleries([]);
      setIsLoading(false);
      return [];
    }
  }, []);

  const updateGalleryStatus = useCallback(async (galleryId: number, newStatus: ReviewStatus, reason?: string): Promise<boolean> => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'You must be logged in to perform approval actions',
        variant: 'destructive'
      });
      return false;
    }

    try {
      // Update only the review_status field (approved_by and approved_at don't exist in galleries table)
      const updateData = {
        review_status: newStatus,
        ...(reason && { rejection_reason: reason })
      };

      const { error } = await supabase
        .from('galleries')
        .update(updateData)
        .eq('id', galleryId);
      
      if (error) {
        console.error('Update error:', error);
        toast({
          title: 'Error',
          description: `Failed to ${newStatus} gallery: ${error.message}`,
          variant: 'destructive'
        });
        return false;
      }

      // Log the approval action in audit_log for tracking attribution
      try {
        const auditData = {
          table_name: 'galleries',
          record_id: galleryId.toString(),
          action: newStatus,
          user_id: user.id,
          changes: {
            review_status: newStatus,
            previous_status: 'pending',
            ...(reason && { reason })
          }
        };

        await supabase
          .from('audit_log')
          .insert(auditData);
      } catch (auditError) {
        // Don't fail the main operation if audit logging fails
        console.warn('Failed to log approval action:', auditError);
      }
      
      toast({
        title: 'Success',
        description: `Gallery ${newStatus} successfully`,
        variant: 'default'
      });
      
      // Update local state
      setPendingGalleries(prev => prev.filter(gallery => gallery.id !== galleryId));
      return true;
    } catch (error) {
      console.error(`Error updating gallery status to ${newStatus}:`, error);
      toast({
        title: 'Error',
        description: `Failed to ${newStatus} gallery: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: 'destructive'
      });
      return false;
    }
  }, [toast, user]);

  const approveGallery = useCallback(async (galleryId: number, reason?: string): Promise<boolean> => {
    return updateGalleryStatus(galleryId, 'approved', reason);
  }, [updateGalleryStatus]);

  const rejectGallery = useCallback(async (galleryId: number, reason?: string): Promise<boolean> => {
    return updateGalleryStatus(galleryId, 'rejected', reason);
  }, [updateGalleryStatus]);

  const flagGallery = useCallback(async (galleryId: number, reason?: string): Promise<boolean> => {
    // For galleries, we keep them as pending but log the flag action
    return updateGalleryStatus(galleryId, 'pending', reason);
  }, [updateGalleryStatus]);

  return {
    pendingGalleries,
    isLoading,
    error,
    fetchPendingGalleries,
    approveGallery,
    rejectGallery,
    flagGallery,
    updateGalleryStatus
  };
};
